import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { 
  DollarSign, TrendingUp, Clock, CheckCircle, Calendar,
  ChevronLeft, ChevronRight, Store, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

export default function SalesEarnings() {
  const { api, user } = useAuth();
  const { formatPrice, currencyConfig } = useCurrency();
  const [earnings, setEarnings] = useState({
    total_earnings: 0,
    pending_earnings: 0,
    paid_earnings: 0,
    monthly_earnings: 0
  });
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('commissions');

  useEffect(() => {
    fetchEarningsData();
  }, []);

  const fetchEarningsData = async () => {
    try {
      // Try to get earnings from API
      try {
        const response = await api.get('/salesperson/earnings');
        setEarnings({
          total_earnings: response.data.total_earnings || 0,
          pending_earnings: response.data.pending_earnings || 0,
          paid_earnings: response.data.paid_earnings || 0,
          monthly_earnings: response.data.monthly_earnings || 0
        });
        setCommissions(response.data.commissions || []);
        setPayouts(response.data.payouts || []);
      } catch (e) {
        // Fallback: Get commission data directly
        console.log('Earnings endpoint not available, using fallback');
      }
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded w-48 animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-earnings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Earnings</h1>
        <p className="text-slate-500 mt-1">Track your commission earnings and payouts</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Total Earnings</p>
                <p className="text-3xl font-bold text-green-700">{formatCurrency(earnings.total_earnings)}</p>
                <p className="text-xs text-green-500">Lifetime earnings</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Pending</p>
                <p className="text-3xl font-bold text-amber-700">{formatCurrency(earnings.pending_earnings)}</p>
                <p className="text-xs text-amber-500">Awaiting payout</p>
              </div>
              <Clock className="w-10 h-10 text-amber-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Paid Out</p>
                <p className="text-3xl font-bold text-blue-700">{formatCurrency(earnings.paid_earnings)}</p>
                <p className="text-xs text-blue-500">Successfully paid</p>
              </div>
              <CheckCircle className="w-10 h-10 text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">This Month</p>
                <p className="text-3xl font-bold text-purple-700">{formatCurrency(earnings.monthly_earnings)}</p>
                <p className="text-xs text-purple-500">Commission earned</p>
              </div>
              <TrendingUp className="w-10 h-10 text-purple-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Rate Info */}
      <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold">Commission Rate: 0.3%</h3>
              <p className="text-purple-200 text-sm">You earn 0.3% of the platform's 10% commission on every delivery order from your restaurants</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-purple-200">Example: {formatPrice(100)} order</p>
              <p className="font-semibold">Platform: {formatPrice(10)} → You: {formatPrice(0.03)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <Button
          variant={activeTab === 'commissions' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('commissions')}
          className={activeTab === 'commissions' ? 'btn-primary' : ''}
        >
          Commission History
        </Button>
        <Button
          variant={activeTab === 'payouts' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('payouts')}
          className={activeTab === 'payouts' ? 'btn-primary' : ''}
        >
          Payouts
        </Button>
      </div>

      {/* Commission History */}
      {activeTab === 'commissions' && (
        <Card>
          <CardHeader>
            <CardTitle>Commission History</CardTitle>
            <CardDescription>Commission earned from your restaurants' orders</CardDescription>
          </CardHeader>
          <CardContent>
            {commissions.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl">
                <DollarSign className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No commissions yet</h3>
                <p className="text-slate-500">Commission records will appear here when your restaurants receive orders</p>
              </div>
            ) : (
              <div className="space-y-4">
                {commissions.map((commission, idx) => (
                  <div 
                    key={commission.id || idx}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <ArrowUpRight className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{commission.restaurant_name || 'Restaurant'}</p>
                        <p className="text-sm text-slate-500">Order #{commission.order_id?.slice(0, 8)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">+{formatCurrency(commission.sales_commission)}</p>
                      <p className="text-xs text-slate-500">{formatDate(commission.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payouts */}
      {activeTab === 'payouts' && (
        <Card>
          <CardHeader>
            <CardTitle>Payout History</CardTitle>
            <CardDescription>Your commission payouts via Stripe Connect</CardDescription>
          </CardHeader>
          <CardContent>
            {payouts.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl">
                <CheckCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No payouts yet</h3>
                <p className="text-slate-500">Payout records will appear here after admin processes your earnings</p>
              </div>
            ) : (
              <div className="space-y-4">
                {payouts.map((payout, idx) => (
                  <div 
                    key={payout.id || idx}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        payout.status === 'completed' ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        {payout.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Payout #{payout.id?.slice(0, 8)}</p>
                        <p className="text-sm text-slate-500">{payout.orders_count || 0} orders</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">{formatCurrency(payout.amount)}</p>
                      <Badge variant={payout.status === 'completed' ? 'default' : 'secondary'}>
                        {payout.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
